<template>
  <div v-if="showModule" class="survey-viewer-module">
    <Toast position="bottom-center" />

    <div class="survey-viewer-content" @wheel.stop>
      <div v-if="loading" class="survey-viewer-state">
        <div class="survey-viewer-skeleton" aria-hidden="true">
          <div class="survey-viewer-skeleton-card">
            <div class="survey-viewer-skeleton-header">
              <Skeleton width="16rem" height="1.15rem" borderRadius="999px" />
              <Skeleton width="28rem" height="0.9rem" borderRadius="999px" />
            </div>

            <div class="survey-viewer-skeleton-grid">
              <div v-for="index in 8" :key="index" class="survey-viewer-skeleton-field">
                <Skeleton width="6.5rem" height="0.85rem" borderRadius="999px" />
                <Skeleton width="100%" height="2.8rem" borderRadius="14px" />
              </div>
            </div>

            <div class="survey-viewer-skeleton-block">
              <Skeleton width="8rem" height="0.85rem" borderRadius="999px" />
              <Skeleton width="100%" height="7.5rem" borderRadius="18px" />
            </div>

            <div class="survey-viewer-skeleton-actions">
              <Skeleton width="7rem" height="2.5rem" borderRadius="999px" />
              <Skeleton width="8rem" height="2.5rem" borderRadius="999px" />
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="error" class="survey-viewer-state">
        <EmptyState
          :title="t('wkt.client.survey.error-state.title')"
          :description="t('wkt.client.survey.error-state.description')"
        />
        <Button
          class="survey-viewer-retry"
          :label="t('wkt.client.survey.actions.retry')"
          @click="loadSurveyInfo"
        />
      </div>

      <div v-else class="survey-viewer-frame-shell">
        <iframe
          ref="viewerFrame"
          class="survey-viewer-frame"
          :style="frameStyle"
          :src="viewerSrc"
          frameborder="0"
          scrolling="no"
          @load="onFrameLoad"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { worktimeApi } from "@wkt/api/worktime";
import type {
  SurveySubmitResponseDTO,
  SurveyViewerInfoDTO,
  SurveyViewerModulePayload,
} from "@wkt/api/surveyDTO";
import EmptyState from "@wkt/components/common/EmptyState.vue";
import { gwtApi } from "@wkt/api/gwt/gwtAPI";
import { useAlert } from "@wkt/composables/useAlert";
import discovery from "../../../discovery";

const FORM_SUBMISSION_ROLE_EMPLOYEE = "EMPLOYEE";

const { t, locale } = useI18n();
const { showAlert } = useAlert();

const showModule = ref(false);
const loading = ref(true);
const error = ref(false);
const currentSurveyId = ref<number | null>(null);
const currentTitle = ref<string>("");
const currentFormId = ref<number | null>(null);
const currentDefinition = ref<string | null>(null);
const currentLastSubmission = ref<string | null>(null);
const frameHeight = ref<number | null>(null);
const viewerFrame = ref<HTMLIFrameElement | null>(null);
const callbackId = `formio-viewer-${crypto.randomUUID()}`;
const frameLoaded = ref(false);
let fallbackTimer: number | null = null;

const viewerSrc = computed(
  () => `form-viewer.html?callbackId=${encodeURIComponent(callbackId)}&lang=${encodeURIComponent(locale.value || "es")}`
);

const frameStyle = computed(() => {
  if (frameHeight.value === null) {
    return null;
  }

  return {
    height: `${frameHeight.value}px`,
  };
});

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function resolveSurveyId(payload?: unknown): number | null {
  const direct = toNumber(payload);
  if (direct !== null) {
    return direct;
  }

  if (payload && typeof payload === "object" && "surveyId" in payload) {
    const nested = toNumber((payload as { surveyId?: unknown }).surveyId);
    if (nested !== null) {
      return nested;
    }
  }

  const hash = window.location.hash || "";
  const match = hash.match(/(?:[;?&])surveyId=([^;&]+)/i);
  if (!match?.[1]) {
    return null;
  }

  return toNumber(decodeURIComponent(match[1]));
}

function isSurveyPayload(payload: unknown): payload is SurveyViewerModulePayload {
  return Boolean(payload) && typeof payload === "object";
}

function encodeBase64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return window.btoa(binary);
}

function scheduleFallbackLoad(): void {
  if (fallbackTimer !== null) {
    window.clearTimeout(fallbackTimer);
  }

  if (currentSurveyId.value === null || currentDefinition.value) {
    return;
  }

  fallbackTimer = window.setTimeout(() => {
    if (loading.value && currentDefinition.value === null) {
      loadSurveyInfo();
    }
  }, 1500);
}

function clearFallbackLoad(): void {
  if (fallbackTimer !== null) {
    window.clearTimeout(fallbackTimer);
    fallbackTimer = null;
  }
}

function buildFramePayload() {
  return {
    type: "ready",
    definition: currentDefinition.value || "{}",
    lastSubmission: currentLastSubmission.value || "{}",
    role: FORM_SUBMISSION_ROLE_EMPLOYEE,
    sessionInfo: gwtApi.getSessionInformation(),
    locale: locale.value || "es",
    service: {
      baseUrl: discovery.worktime_service_url,
      headers: worktimeApi.getCommonHeaders().headers ?? {},
    },
  };
}

function postToFrame(payload: unknown): void {
  const frameWindow = viewerFrame.value?.contentWindow;
  if (!frameWindow) {
    return;
  }

  frameWindow.postMessage(payload, "*");
}

function syncFormWithFrame(): void {
  if (!frameLoaded.value || !currentDefinition.value) {
    return;
  }

  postToFrame(buildFramePayload());
}

function applyFrameHeight(payload: unknown): void {
  const rawValue =
    typeof payload === "number"
      ? payload
      : typeof payload === "string"
        ? Number.parseFloat(payload)
        : NaN;

  if (!Number.isFinite(rawValue) || rawValue <= 0) {
    return;
  }

  frameHeight.value = Math.max(Math.ceil(rawValue) + 60, 640);
}

function applySurveyInfo(info?: SurveyViewerInfoDTO | null): void {
  currentSurveyId.value = toNumber(info?.survey?.id) ?? currentSurveyId.value;
  currentTitle.value = info?.survey?.title?.trim() || currentTitle.value;
  currentFormId.value = toNumber(info?.form?.id);
  currentDefinition.value = info?.form?.definition?.trim() || null;
  currentLastSubmission.value = info?.lastSubmission?.submissionData?.trim() || null;
  loading.value = false;
  error.value = false;
  clearFallbackLoad();
  syncFormWithFrame();
}

function applyPayload(payload?: unknown): void {
  if (!isSurveyPayload(payload)) {
    currentSurveyId.value = resolveSurveyId(payload);
    currentTitle.value = "";
    currentFormId.value = null;
    currentDefinition.value = null;
    currentLastSubmission.value = null;
    loading.value = currentSurveyId.value !== null;
    error.value = currentSurveyId.value === null;
    scheduleFallbackLoad();
    return;
  }

  currentSurveyId.value = resolveSurveyId(payload);
  currentTitle.value = payload.title?.trim() || "";
  currentFormId.value = toNumber(payload.form?.id);
  currentDefinition.value = payload.form?.definition?.trim() || null;
  currentLastSubmission.value = payload.lastSubmission?.submissionData?.trim() || null;
  loading.value = Boolean(payload.loading);
  error.value = Boolean(payload.error) || currentSurveyId.value === null;

  if (!loading.value && currentDefinition.value) {
    clearFallbackLoad();
  } else {
    scheduleFallbackLoad();
  }

  syncFormWithFrame();
}

function loadSurveyInfo(): void {
  if (currentSurveyId.value === null) {
    error.value = true;
    loading.value = false;
    return;
  }

  loading.value = true;
  error.value = false;

  worktimeApi.getFormSurveyInfo(
    currentSurveyId.value,
    (response) => {
      if (!response?.survey || !response?.form) {
        error.value = true;
        loading.value = false;
        return;
      }
      applySurveyInfo(response);
    },
    () => {
      error.value = true;
      loading.value = false;
    }
  );
}

function goHome(): void {
  window.location.hash = "#home;refresh=true";
}

function handleSubmitResponse(response: SurveySubmitResponseDTO): void {
  if (response?.rsOK) {
    goHome();
    return;
  }

  showAlert({
    text: response?.message || t("wkt.client.survey.error-state.description"),
    color: "error",
  });
}

function submitSurvey(submitted: boolean, submissionPayload: unknown): void {
  const surveyId = currentSurveyId.value;
  const sessionInformation = gwtApi.getSessionInformation();
  const employeeId = sessionInformation?.employee?.id;

  if (
    surveyId === null ||
    currentFormId.value === null ||
    employeeId === null ||
    employeeId === undefined
  ) {
    showAlert({
      text: t("wkt.client.survey.error-state.description"),
      color: "error",
    });
    return;
  }

  const serializedPayload = JSON.stringify(submissionPayload ?? {});

  loading.value = true;
  error.value = false;

  worktimeApi.storeFormSurveySubmission(
    surveyId,
    {
      employeeId,
      creationDate: new Date().toISOString(),
      formId: currentFormId.value,
      submissionData: encodeBase64(serializedPayload),
      role: FORM_SUBMISSION_ROLE_EMPLOYEE,
      name: currentTitle.value || "---",
      submitted,
    },
    (response) => {
      loading.value = false;
      handleSubmitResponse(response);
    },
    (requestError) => {
      const responseData = requestError?.response?.data as
        | { message?: string | null }
        | undefined;

      loading.value = false;
      showAlert({
        text: responseData?.message || requestError?.message || t("wkt.client.survey.error-state.description"),
        color: "error",
      });
    }
  );
}

function onFrameLoad(): void {
  frameLoaded.value = true;
  syncFormWithFrame();
}

function onMessage(event: MessageEvent): void {
  const data = event.data;
  if (!data || typeof data !== "object" || data.target !== callbackId) {
    return;
  }

  if (data.type === "ready") {
    syncFormWithFrame();
    return;
  }

  if (data.type === "fix-height") {
    applyFrameHeight(data.payload);
    return;
  }

  if (data.type === "submit" || data.type === "store") {
    submitSurvey(data.type === "submit", data.payload);
  }
}

function onReveal(payload?: unknown): void {
  showModule.value = true;
  frameLoaded.value = false;
  frameHeight.value = null;
  applyPayload(payload);
}

function refreshModule(): void {
  loadSurveyInfo();
}

onMounted(() => {
  window.addEventListener("message", onMessage);
});

onUnmounted(() => {
  clearFallbackLoad();
  window.removeEventListener("message", onMessage);
});

defineExpose({
  onReveal,
  refreshModule,
});
</script>

<style scoped>
.survey-viewer-module,
.survey-viewer-content,
.survey-viewer-frame-shell {
  min-height: 100%;
}

.survey-viewer-content {
  min-height: calc(100vh - 74px);
}

.survey-viewer-state {
  min-height: calc(100vh - 94px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px 12px 24px;
}

.survey-viewer-skeleton {
  width: min(1120px, 100%);
  display: flex;
  flex-direction: column;
}

.survey-viewer-skeleton-card {
  width: 100%;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 20px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.06);
}

.survey-viewer-skeleton-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.survey-viewer-skeleton-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 20px;
}

.survey-viewer-skeleton-field,
.survey-viewer-skeleton-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.survey-viewer-skeleton-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.survey-viewer-retry {
  margin-top: 16px;
}

.survey-viewer-frame-shell {
  padding: 0;
}

.survey-viewer-frame {
  display: block;
  width: 100%;
  min-height: calc(100vh - 74px);
  border: 0;
  background: #ffffff;
  overflow: hidden;
}

@media (max-width: 767px) {
  .survey-viewer-state {
    min-height: calc(100vh - 84px);
    padding: 12px 8px 16px;
  }

  .survey-viewer-skeleton-card {
    border-radius: 16px;
    padding: 16px 14px;
    gap: 16px;
    box-shadow: none;
  }

  .survey-viewer-skeleton-header {
    gap: 10px;
  }

  .survey-viewer-skeleton-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .survey-viewer-skeleton-actions {
    justify-content: flex-start;
  }

  .survey-viewer-frame {
    min-height: calc(100vh - 64px);
  }
}
</style>
